<template>
	<div class="container bg-white mx-auto pl-0">
		<div v-if="userInfo">
			<MyAccountHeader
				:fname="userInfo.firstName"
				:address="userInfo.address1"
				:city="userInfo.city"
				:state="userInfo.state"
				:zip="userInfo.zip"
				:customer_id="userInfo.cust_No"
				:showLogin="false"
			/>

			<div class="grid md:grid-cols-3 gap-4 md:gap-16">
				<BillingSidebar
					:franchiseName="franchiseName"
					:franchiseAddress="franchiseAddress"
					:franchisePhone="franchisePhone"
					redirectPath="/pay-a-bill"
				/>
				<div class="md:col-span-2">
					<div class="border rounded invoice mb-6">
						<h2 class="px-12 mb-5">Pay a bill</h2>
						<p v-if="isAutoPay" class="pl-6">Our records indicate that you have enrolled in Auto Pay. Payments made here will be in additionto those automatically charged after each service</p>
						<div v-if="!completedOrder">
							<Modal v-if="errors.length && errorModalVisible" @hideModal="hideModal" :wide="true" id="carousel-modal">
								<div class="px-12 py-6 red">
									<b>Please correct the following error(s):</b>
									<ul>
										<li class="red" v-for="(error, index) in errors" :key="index">
											{{ error }}
										</li>
									</ul>
								</div>
							</Modal>
							<form id="checkout_form">
								<div class="px-12 py-5">
									<div v-if="currentBalance">
										<h3 class="h2 mb-5">Amount due:</h3>
										<div class="grid grid-cols-2">
											<label
												id="label-pay-type-balance"
												class="radio"
												for="pay-type-balance"
												:class="{ current: payType == 'balance' }"
                    							@click="iframeUrl = null"
											>
												<input
													id="pay-type-balance"
													type="radio"
													name="payType"
													value="balance"
													v-model="payType"
												/>
												<span class="ml-5">Pay current balance</span>
											</label>
											<div class="text-right">
												<span class="price red">{{ formatPrice(currentBalance) }}</span>
											</div>
										</div>
										<hr class="my-5" />
									</div>
									<div class="grid grid-cols-1 lg:grid-cols-2 items-center">
										<label
											id="label-pay-type-other"
											class="radio"
											for="pay-type-other"
											v-bind:class="{ current: payType == 'other' }"
                    						@click="iframeUrl = null"
										>
											<input
												id="pay-type-other"
												type="radio"
												name="payType"
												value="other"
												v-model="payType"
											/>
											<span class="ml-5">Pay <span v-if="currentBalance">another</span> amount</span>
										</label>
										<div class="text-right">
											<input 
												type="text"
												class="amt"
												placeholder="Amount"
												v-model.lazy="paymentAmount" 
												v-money="moneyConfig" 
												:disabled="payType == 'balance'"
												required
												:class="{'error': validationErrors.includes('payAmount') && payType != 'balance'}"
												@focus="clearValidationError('payAmount')" />
										</div>
									</div>
									<hr class="my-3" />

									<div>
										<p class="text-right">
											<input
												type="button"
												class="button"
												value="Continue to Payment"
												title="Choose an amount above to continue."
												@click.stop.prevent="requestIframeUrl()"
											/>
										</p>
									</div>
									<div v-if="iframeUrl">
										<iframe id="payment-iframe" width="100%" height="930px"
											:src="iframeUrl"
											style="background-color: transparent; border: none;"
										>
										</iframe>
									</div>

									<div v-if="showForm">
										<CheckoutBillingInfo ref="billingInfoRef" :requirePhone="true" />
										<div class="flex justify-center md:justify-start">
											<label
												class="checkbox mt-3 mb-4"
												for="enrollAutopay"
											>
												<span class="checkbox__input">
													<input
														type="checkbox"
														id="enrollAutopay"
														name="checkbox"
														checked
														v-model="enrollAutoPay"
													/>
													<span class="checkbox__control">
														<svg
															xmlns="http://www.w3.org/2000/svg"
															viewBox="0 0 24 24"
															aria-hidden="true"
															focusable="false"
														>
															<path
																fill="none"
																stroke="currentColor"
																stroke-width="3"
																d="M1.73 12.91l6.37 6.37L22.79 4.59"
															/>
														</svg>
													</span>
												</span>
												<p class="text-sm my-2">Please store my card on file to setup automatic payments for future completed services.</p>
											</label>
										</div>
										<p class="text-center md:text-right">
											<input
												type="submit"
												class="button"
												value="Make a Payment"
												@click.stop.prevent="makePayment()"
											/>
										</p>
									</div>
								</div>
							</form>
						</div>
						<div v-else-if="thankYouMessage" class="px-12 py-5">
							<h3 class="h2 mb-5">{{ thankYouMessage.intro_title }}</h3>
							<div v-html="thankYouMessageEmail"></div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import _ from 'lodash';
import { getFormattedPrice } from "~/utils/payment";
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import type { Store } from 'vuex/types/index.js';
import type State from '~/domains/store/State';
import Swal from 'sweetalert2';
import { getUserInfo, type UserInfo } from "~/utils/customer";
import CheckoutBillingInfo from '~/components/checkout/BillingInfo.vue';
import { Money3Directive } from 'v-money3';

const vMoney = Money3Directive;
const { $customers, $sg, $payments, $orders, $userContext } = useNuxtApp();
const route = useRoute();
const router = useRouter();
const store: Store<State> = useStore();

const errors = ref<string[]>([]);
const validationErrors = ref<string[]>([]);
const paymentAmount = ref("0");
const userInfo = ref<UserInfo>();
const iframeUrl = ref(null);
const paymentId = ref(null);
const paymentBody = ref({});
const showForm = ref(false);
const franchiseInfo = ref();
const franchiseName = ref("");
const franchiseAddress = ref("");
const franchisePhone = ref("");
const currentBalance = ref(0);
const completedOrder = ref(false);
const confirmationEmail = ref("");
const pendingTransaction = ref(false);
const payType = ref("other");
const thankYouMessage = ref<{ intro_title: string, intro_text: string }>();
const enrollAutoPay = ref(false);
const isAutoPay = ref(false);
const errorModalVisible = ref(false);
const moneyConfig = {
	decimal: '.',
	thousands: ',',
	prefix: '$',
	suffix: '',
	precision: 2,
	masked: false,
	disableNegative: true,
};

const billingInfoRef = useTemplateRef("billingInfoRef");

const { data: headObject } = await useAsyncData('meta', async ({ $metadata }) => {
  return await $metadata.createHeadObject()
})

useHead(headObject)

const thankYouMessageEmail = computed(() => {
	return thankYouMessage.value?.intro_text.replace(
		"{email}",
		confirmationEmail.value
	);
});

const paymentAmountNumber = computed(() => {
	return Number(paymentAmount.value?.replaceAll(/[$,]+/g, "").trim());
});

const amount = computed(() => {
  	return payType.value === "balance" ? currentBalance.value : paymentAmountNumber.value;
});

const formatPrice = (value: number) => {
	return getFormattedPrice(value);
};

const enrollInAutoPay = async () => {
	const persistedState = store.state.persistedState;
	const billingInfoData = billingInfoRef.value.getBillingInfo();
	const data = {
		cardHolderName: billingInfoData.name,
		cardHolderAddress: billingInfoData.address1,
		cardHolderCity: billingInfoData.city,
		cardHolderState: billingInfoData.state,
		zipCode: billingInfoData.zip.slice(0, 5),
		creditCardNumber: billingInfoData.ccNumber,
		creditCardExpirationMonth: billingInfoData.ccMonth,
		creditCardExpirationYear: billingInfoData.ccYear,
		creditCardType: billingInfoData.ccType,
		cvV2: billingInfoData.ccSecurityCode,
	};

	const autoPay = await $payments
		.enrollAutoPay(
			persistedState.scope.sqldbid,
			persistedState.scope.cust_No,
			data
		)
		.then(response => response)
		.catch(error => ({ error: error, response: error.response.data }));

	if (autoPay === undefined) {
		errors.value.push(
			"We're sorry, there was an issue enrolling in Auto Pay."
		);
	}
};

const validatePaymentForm = async () => {
	if (
		(payType.value === "other" && paymentAmountNumber.value <= 0) ||
		(payType.value === "balance" && (currentBalance.value <= 0))
	) {
		errors.value.push("No amount was entered for payment.");
		validationErrors.value.push('payAmount');
	}

	if (Number(paymentAmountNumber.value) > 5000) {
		const result = await Swal.fire({
			title: "Confirm amount",
			text: "You entered a payment amount of over $5,000, is this accurate?",
			showDenyButton: true,
			confirmButtonText: "Yes",
			denyButtonText: "No",
			background: "#eef1f4",
			customClass: {
				popup: "swal2-border-radius border-2 border-green-600",
				confirmButton: "swal2-border-radius",
				denyButton: "swal2-border-radius",
			}
		});

		if (!result.isConfirmed) {
			errors.value.push("Confirm intended payment amount.");
		}
	}

	errors.value.push(...billingInfoRef.value.validate());
};

const makePayment = async () => {
	if (pendingTransaction.value) return;

	try {
		pendingTransaction.value = true;

		errors.value = [];

		await validatePaymentForm();

		if (errors.value.length > 0) {
			return;
		}

		store.commit("SET_SHOW_LOADER", true);

		const billingInfoData = billingInfoRef.value.getBillingInfo();

		const amount = payType.value === "balance" ? currentBalance.value : paymentAmountNumber.value;

		const persistedState = store.state.persistedState;

		const order = await $sg.createBillPayOrder(
			store.state.persistedState.scope,
			Number(amount),
			persistedState.zip?.slice(0, 5).trim(),
			false,
			null,
		);

		if (!order) {
			errors.value.push("We're sorry. We could not create a new order for you.");
			return;
		}

		const data = {
			paymentType: "BillPay",
			customerOrderNumber: order.id,
			billToAddress1: billingInfoData.address1,
			billToCity: billingInfoData.city,
			billToState: billingInfoData.state,
			billToZip: billingInfoData.zip.slice(0, 5),
			billToEmail: billingInfoData.email,
			cardHolderName: billingInfoData.name,
			transactionAmount: Number(amount),
			creditCardNumber: billingInfoData.ccNumber,
			creditCardExpirationMonth: billingInfoData.ccMonth,
			creditCardExpirationYear: billingInfoData.ccYear,
			cvV2: billingInfoData.ccSecurityCode,
			creditCardType: billingInfoData.ccType,
			comment: billingInfoData.note,
			AllowText: billingInfoData.AllowText,
		};

		const billing = await $payments
			.payByCreditCard(data, persistedState.scope.id)
			.then(response => response)
			.catch(error => error);
		
		if (billing === undefined || billing.error) {
			errors.value.push("We're sorry, there was an issue processing your card.");
			//Update Order with failed payment error
			order.comment = (
				`${billing?.response?.Message ?? "Credit card payment processing error"} ${billing?.response?.StackTraceString ?? ""}`
			).substring(0, 250);
			$orders.updateOrder(order, store.state.persistedState.scope.id);
		} else {
			if (enrollAutoPay.value) {
				await enrollInAutoPay();
			}
			confirmationEmail.value = billingInfoData.email;
			completedOrder.value = true;
		}
	} catch (err) {
		console.error(err);
		errors.value.push("An error occurred, please try again later.");
	} finally {
		pendingTransaction.value = false;
		store.commit("SET_SHOW_LOADER", false);
		errorModalVisible.value = errors.value.length > 0;
	}
};

const hideModal = () => {
	errorModalVisible.value = false;
};

const clearValidationError = (fieldName: string) => {
	const errorIndex = validationErrors.value.indexOf(fieldName);
	if (errorIndex !== -1) {
		validationErrors.value.splice(errorIndex, 1);
	}
};

const warningSwal = (title: string) => {
	Swal.fire({
		title: title,
		icon: "warning",
		customClass: {
			popup: "swal2-border-radius",
			confirmButton: "swal2-border-radius bg-sgreen",
		},
	});
};

const requestIframeUrl = async () => {
	if (amount.value <= 0) {
		warningSwal("Please choose an amout greater than $0.00.");
		return;
	}
	if (amount.value > 5000) {
		const result = await Swal.fire({
			title: "Confirm amount",
			text: "You entered a payment amount of over $5,000, is this accurate?",
			showDenyButton: true,
			confirmButtonText: "Yes",
			denyButtonText: "No",
			background: "#eef1f4",
			customClass: {
				popup: "swal2-border-radius",
				confirmButton: "swal2-border-radius",
				denyButton: "swal2-border-radius",
			}
		});

		if (!result.isConfirmed) {
			warningSwal("Confirm intended payment amount.");
			return;
		}
  	}
	const scope = store.state.persistedState.scope;
	const body = {
		sqldbid: scope.sqldbid,
		CustomerNumber: scope.cust_No,
		Amount: amount.value,
		AchOptionNotAvailable: $payments.getAchOptionNotAvailable(),
		DisableAmountField: true,
		HideAutopayOption: isAutoPay.value
	};

	const order = await $sg.createBillPayOrder(
		scope,
		Number(amount.value),
		store.state.persistedState.zip?.slice(0, 5).trim(),
		false,
		null,
	);
	if (!order) {
		errors.value.push("We're sorry. We could not create a new order for you.");
		return;
	}
	paymentBody.value = {
		sqldbid: scope.sqldbid,
		customerNumber: scope.cust_No,
		paymentType: "BillPay",
		billToEmail: store.state.persistedState.billingEmail,
		customerOrderNumber: order.id,
		transactionAmount: amount.value
	};

	await $payments.getPaymentUrl(body)
  	.then(response => {
		iframeUrl.value = response.data.message;
	}).catch(error => {
		warningSwal("We're sorry, there was an issue connecting to the payment gateway. Please try again later.");
	});

	if (!iframeUrl.value) return;

	try {
		const paymentIdResponse = await $payments.getPaymentId(paymentBody.value);
		if (paymentIdResponse?.data?.id) {
			paymentId.value = paymentIdResponse.data.id;
		} else {
			throw new Error("Payment ID response did not contain an id.");
		}
	} catch (error) {
		console.error("Error fetching payment id response:", error);
		iframeUrl.value = null;
		paymentId.value = null;
		await Swal.fire({
			title: "Unable to initialize payment session. Please try again later.",
			icon: "warning",
			customClass: {
				popup: "swal2-border-radius",
				confirmButton: "swal2-border-radius bg-sgreen",
			},
		});
		router.push("/");
	}
};

const receiveMesage = async (event) => {
  let response = event.data.response;
  console.log("Received message from iframe:", response);
  if (response == null) {
    //TODO: handle error
    return;
  } else if (response.Success == false && response.Message == "IFrame Closed") {
    iframeUrl.value = null;
  } else if (response.Success == true && response.Model) {
    iframeUrl.value = null;
    console.log("Payment successful");
		if (!paymentId.value) {
			console.error("Payment received from iframe but no paymentId is available. This should not happen.");
			warningSwal("Payment was made yet there was a post processing issue.");
			const email = store.state.persistedState.billingEmail || userInfo.value?.email || "";
			router.push("/");
			return;
		}

	response.Model.AutopayResult = false; // 4371858133 - Override payment response model "AutopayResult" for bill pay payments

	$payments.postPaymentResp(paymentId.value, response.Model)
		.then(res => {
			console.log("Post Payment Response:", res);
			Swal.fire({
				title: "Your payment was processed successfully.",
				icon: "success",
				customClass: {
					popup: "swal2-border-radius",
					confirmButton: "swal2-border-radius bg-sgreen",
				},
			});
		}).catch(error => {
			console.log("Error posting payment response:", error);
			warningSwal("Payment was made yet there was a post processing issue.");
		}).finally(() => {
			const email = store.state.persistedState.billingEmail || userInfo.value?.email || "";
  			router.push(`/checkout/thank-you?email=${email}`);
		});
  }
};

onMounted(async () => {
	const custNo = route.query.cust_no as string;
	const zip = route.query.zip as string;
	const lastName = route.query.lastName as string;
	const amount = Number(_.replace(route.query.amount as string, /[^\d\.]/g, ""));

	if (custNo && zip && lastName) {
		const customer = await $customers.getCustomerByInfo(
			custNo.trim(),
			lastName.trim(),
			zip.trim()
		);

		errors.value = [];

		if (customer) {
			if (
				store.state &&
				store.state.persistedState &&
				store.state.persistedState.sqldbid &&
				store.state.persistedState.cust_No == Number(custNo.trim())
			) {
				store.commit("SET_CUSTOMER", customer);
			} else {
				const persistedData = {
					sqldbid: customer.sqldbid,
					cust_No: customer.cust_No,
					customer: customer,
					zip: zip.trim(),
				};
				store.commit("SET_STATE", persistedData);
			}

			await $sg.createScope(
				useNuxtApp(),
				customer.sqldbid,
				customer.cust_No
			);
		}
	}

	await $userContext.metaRefresh({
		refresh: new Set(["PAYMENT_INFO"]),
	});

	if (!$payments.getEnabledPayTypes().includes("billPay")) {
		router.push("/");
		return;
	}

	if (amount > 0) {
		paymentAmount.value = amount.toFixed(2);
	}

	const persistedState = store.state.persistedState;
	if (!persistedState || !persistedState.customer) {
		router.push("/account");
		return;
	}

	franchiseInfo.value = await $sg.getApiData(
		`Companies/SG/Franchise/SQLDBID/${persistedState.sqldbid}`
	);
	franchiseName.value = franchiseInfo.value.compName;
	franchiseAddress.value =
		franchiseInfo.value.address1 +
		"<br />" +
		franchiseInfo.value.address2 +
		(franchiseInfo.value.address3
			? "<br />" + franchiseInfo.value.address3
			: "");
	franchisePhone.value = franchiseInfo.value.phone1;

	const customer = persistedState.customer;
	const customerBalance = (customer.balance ?? 0);
	currentBalance.value = customerBalance > 0 ? customerBalance : 0;

	if (amount <= 0 && currentBalance.value > 0) {
		payType.value = "balance";
	}

	isAutoPay.value = persistedState?.customer?.contactPreferences?.autoPay ?? false;
	thankYouMessage.value = await $sg.getOctoberData(
		"pages/checkoutthankyou"
	);

	userInfo.value = getUserInfo(persistedState);
	
  	window.addEventListener("message", receiveMesage, false);
});

onUnmounted(() => {
  	window.removeEventListener("message", receiveMesage);
});
</script>

<style scoped>
.invoice h2 {
	background: #00810e;
	color: #fff;
	border-top-right-radius: 26px;
	border-top-left-radius: 26px;
	line-height: 70px;
}

.cards {
	margin: 0;
	padding: 0;
	text-align: right;
}

.cards li {
	margin: 0 0 0 25px;
	padding: 0;
	list-style: none;
	display: inline-block;
}

.checkbox {
	display: grid;
	grid-template-columns: min-content auto;
	grid-gap: 0.5em;
}

.checkbox__control {
	display: inline-grid;
	width: 1em;
	height: 1em;
	border-radius: 0.25em;
	border: 0.1em solid #00810e;
	background: #fff;
	color: white;
	margin-top: -3px;
}

.checkbox__control svg {
	transition: transform 0.1s ease-in 25ms;
	transform: scale(0);
	transform-origin: bottom left;
}

.checkbox__input {
	display: grid;
	grid-template-areas: "checkbox";
}

.checkbox__input input {
	opacity: 0;
	width: 0;
	height: 0;
}

.checkbox__input input:checked + .checkbox__control svg {
	transform: scale(1);
}

.checkbox__input input:checked + .checkbox__control {
	background: #00810e;
}

input[type="radio"] {
	visibility: hidden;
}

label.radio {
	background: url("~/assets/images/radio.png")
		no-repeat left center / 34px 34px;
	padding-left: 13px;
	line-height: 30px;
	height: 34px;
	cursor: pointer;
}

label.radio.current {
	background: url("~/assets/images/radio-on.png")
		no-repeat left center / 34px 34px;
}
</style>
